<!-- 抽奖码任务列表（白色卡片内部） -->
<script lang="ts">
  import { _ as t } from "svelte-i18n";
  import quesIcon from "../assets/images/ui/ques.png";

  interface Task {
    /** i18n key 前缀 */
    key: string;
    /** 已登记后的行动按钮文案 i18n key */
    actionKey?: string;
    /** 是否有问号提示 */
    tip?: boolean;
  }

  interface Props {
    /** 是否在 App 内（站内） */
    embedded?: boolean;
    /** 站内是否已登记活动 */
    registered?: boolean;
    /** 站内是否不符合活动资格（独立于开户状态） */
    ineligible?: boolean;
    /** 站内开户状态；false 时开放“去开户”，undefined 时保持旧版禁用行为 */
    accountOpened?: boolean;
    /** 点击任务按钮 */
    onAction?: (key: string) => void;
    /** 点击问号 */
    onTip?: (key: string) => void;
  }

  let {
    embedded = false,
    registered = false,
    ineligible = false,
    accountOpened,
    onAction,
    onTip,
  }: Props = $props();

  const tasks: Task[] = [
    { key: "open_account" },
    { key: "deposit", actionKey: "btn_go_deposit", tip: true },
    { key: "trade", actionKey: "btn_go_trade", tip: true },
    { key: "ipo", actionKey: "btn_go_ipo", tip: true },
    { key: "invite", actionKey: "btn_send_invite", tip: true },
    { key: "offline", actionKey: "btn_view_details", tip: true },
  ];

  /**
   * 按钮状态：
   * - 站外：全部“立即獲取”可点（点击唤起/下载 App）
   * - 站内未开户：开户任务显示“去开户”并可点击；已开户或状态未知时保持置灰
   * - 站内不符合活动资格：其余任务显示“不符合條件”，置灰且不可点，点击不做任何响应
   * - 站内已登记：其余任务显示各自行动文案（去入金 / 去交易 / 去認購 / 發送邀請）
   * - 站内未登记且有资格：其余任务显示“立即獲取”
   *
   * dimmed 控制置灰样式，disabled 屏蔽点击，两者分开是因为“立即獲取”置灰时
   * 也要屏蔽点击，而其他状态只需要其中一项。
   */
  function resolveButton(task: Task): {
    btnKey: string;
    disabled: boolean;
    dimmed: boolean;
  } {
    // 线下活动不受站内、登记和资格状态影响，始终可查看详情。
    if (task.key === "offline") {
      return { btnKey: "btn_view_details", disabled: false, dimmed: false };
    }
    if (!embedded) {
      return { btnKey: "btn_claim", disabled: false, dimmed: false };
    }
    if (task.key === "open_account") {
      return accountOpened === false
        ? { btnKey: "btn_open_account", disabled: false, dimmed: false }
        : { btnKey: "btn_claim", disabled: true, dimmed: true };
    }
    if (ineligible) {
      return { btnKey: "btn_ineligible", disabled: true, dimmed: true };
    }
    if (registered) {
      return {
        btnKey: task.actionKey ?? "btn_claim",
        disabled: false,
        dimmed: false,
      };
    }
    return { btnKey: "btn_claim", disabled: false, dimmed: false };
  }
</script>

<div class="flex flex-col gap-[16px] p-[12px]">
  {#each tasks as task (task.key)}
    {@const btn = resolveButton(task)}
    <!-- 设计稿：宽度 fill(319px) / 高度 hug / radius 16 / padding 16 / gap 16 -->
    <div
      class="task-row flex items-center gap-[16px] rounded-[16px] p-[16px]"
    >
      <div class="flex-1 min-w-0">
        <!-- 标题 + 问号 -->
        <div class="flex items-center gap-[8px]">
          <span class="task-title">{$t(`task_${task.key}_title`)}</span>
          {#if task.tip}
            <button
              type="button"
              class="shrink-0"
              aria-label="tip"
              onclick={() => onTip?.(task.key)}
            >
              <img src={quesIcon} alt="" class="tip-icon" />
            </button>
          {/if}
        </div>

        <!-- 描述 -->
        <p class="task-desc mt-[6px]">{$t(`task_${task.key}_desc`)}</p>

        <!-- 抽奖码数量 -->
        <p class="task-code mt-[4px]">
          {$t("code_label")}
          {$t(`task_${task.key}_codes`)}
        </p>
      </div>

      <button
        type="button"
        class="grad-btn task-btn shrink-0"
        class:is-disabled={btn.dimmed}
        disabled={btn.disabled}
        onclick={() => onAction?.(task.key)}
      >
        {$t(btn.btnKey)}
      </button>
    </div>
  {/each}
</div>

<style>
  .task-row {
    background: #e9f2fd;
  }

  /* 标题（设计稿）：16px / 600 / line-height 24px / #1D1D1D */
  .task-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0;
    color: #1d1d1d;
  }

  /* 描述（设计稿）：12px / 400 / line-height 150% / #1D1D1D 60% */
  .task-desc {
    font-size: 12px;
    font-weight: 400;
    line-height: 150%;
    color: rgba(29, 29, 29, 0.6);
  }

  /* 抽奖码（设计稿）：14px / 400 / line-height 22px / #2E4CE4 */
  .task-code {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: #2e4ce4;
  }

  /* 问号图标 16x16，距标题 8px */
  .tip-icon {
    display: block;
    width: 14px;
    height: 14px;
  }

  /*
    任务按钮（设计稿）：宽度 hug（88px）/ 高 32px / min-width 64px
    / radius 104px / padding 5px 16px
    渐变底 + 1.5px 渐变描边 + 内阴影在 app.css 的 .grad-btn 里
  */
  .task-btn {
    gap: 10px;
    min-width: 64px;
    height: 38px;
    padding: 5px 16px;
    border-radius: 104px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }
</style>
